import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Convert VAPID key from base64 URL to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if push notifications are supported
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
    }
  }, []);

  // Check subscription status from backend
  const checkSubscriptionStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications/status`, {
        withCredentials: true
      });
      setIsSubscribed(response.data.subscribed);
    } catch (err) {
      console.error('Error checking subscription status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSupported) {
      checkSubscriptionStatus();
    } else {
      setLoading(false);
    }
  }, [isSupported, checkSubscriptionStatus]);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      setError('Push notifications are not supported in this browser');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // Request notification permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      
      if (permissionResult !== 'granted') {
        setError('Notification permission denied');
        setLoading(false);
        return false;
      }

      // Register service worker
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      await navigator.serviceWorker.ready;
      console.log('Service Worker registered');

      // Get VAPID public key from backend
      const vapidResponse = await axios.get(`${API}/notifications/vapid-public-key`, {
        withCredentials: true
      });
      const vapidPublicKey = vapidResponse.data.publicKey;
      
      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to backend
      const subscriptionJSON = subscription.toJSON();
      await axios.post(`${API}/notifications/subscribe`, {
        endpoint: subscriptionJSON.endpoint,
        keys: subscriptionJSON.keys,
        expirationTime: subscriptionJSON.expirationTime
      }, {
        withCredentials: true
      });

      setIsSubscribed(true);
      console.log('Push notification subscription successful');
      return true;
    } catch (err) {
      console.error('Error subscribing to push notifications:', err);
      setError(err.message || 'Failed to subscribe to push notifications');
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Unsubscribe from push manager
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
      }

      // Remove subscription from backend
      await axios.delete(`${API}/notifications/unsubscribe`, {
        withCredentials: true
      });

      setIsSubscribed(false);
      console.log('Push notification unsubscription successful');
      return true;
    } catch (err) {
      console.error('Error unsubscribing from push notifications:', err);
      setError(err.message || 'Failed to unsubscribe');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Send a test notification
  const sendTestNotification = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/notifications/send-test`, {}, {
        withCredentials: true
      });
      return response.data;
    } catch (err) {
      console.error('Error sending test notification:', err);
      throw err;
    }
  }, []);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    sendTestNotification,
    checkSubscriptionStatus
  };
}
